/*!
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2026 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import type { BrowserConfig, MetaItem } from "@grafana/faro-web-sdk";
import { getSelectorsByUserAgent } from "react-device-detect";

// custom Faro event names, shared by all apps
export const FARO_EVENTS = {
  ORDER_DETAIL_VIEW: "order_detail_view",
  VIEWER_LAUNCH: "viewer_launch",
  STUDY_DOWNLOAD: "study_download",
  REPORT_DOWNLOAD: "report_download",
  SOCIAL_LOGIN: "social_login",
  STUDY_SEARCH: "study_search",
} as const;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_PATTERN = /^\d+$/;
const DICOM_UID_PATTERN = /^\d+(\.\d+)+$/;
const ID_SEGMENT = ":id";

const isIdSegment = (segment: string): boolean => {
  if (
    UUID_PATTERN.test(segment) ||
    NUMERIC_PATTERN.test(segment) ||
    DICOM_UID_PATTERN.test(segment)
  ) {
    return true;
  }
  const digits = segment.replace(/\D/g, "").length;
  if (!digits) return false;
  // long tokens (share links, hashes) or segments made mostly of digits (MRN123456, ORD-2024-0001);
  // route names like "HL7MessageLogs" have too few digits to match.
  return (
    segment.length >= 20 ||
    (segment.length >= 4 && digits / segment.length >= 0.3)
  );
};

/**
 * Builds a low-cardinality Faro view name from a SPA location:
 * id-like path segments become ":id" (/Worklist/Boards/12/ab-34 -> /Worklist/Boards/:id/:id),
 * and only the allow-listed query params are kept (e.g. patient-ui "type=images|report").
 */
export const getFaroViewName = (
  pathname: string,
  search = "",
  queryKeys: string[] = []
): string => {
  const path =
    pathname
      .split("/")
      .map((segment) => {
        let decoded = segment;
        try {
          decoded = decodeURIComponent(segment);
        } catch {
          // keep the raw segment when it is not valid URI encoding
        }
        return segment && isIdSegment(decoded) ? ID_SEGMENT : segment;
      })
      .join("/")
      .replace(/\/+$/, "") || "/";

  if (!queryKeys.length || !search) return path;
  const params = new URLSearchParams(search);
  const kept = queryKeys
    .filter((key) => params.has(key))
    .map((key) => `${key}=${params.get(key)}`);
  return kept.length ? `${path}?${kept.join("&")}` : path;
};

const UNKNOWN = "unknown";

// react-device-detect reports desktop browsers as "browser" and missing values as "none"
const normaliseDeviceValue = (value?: string): string =>
  !value || value === "none" ? UNKNOWN : value;

// device type/brand/model (ua-parser via react-device-detect); browser, OS and viewport
// come from Faro's built-in browser meta
export const faroDeviceMeta: MetaItem = () => {
  const { deviceType, mobileVendor, mobileModel } = getSelectorsByUserAgent(
    navigator.userAgent ?? ""
  );
  // iPadOS 13+ sends a desktop Safari UA; touch support tells it apart from a Mac
  const isIPadOS =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  let type =
    deviceType === "browser" ? "desktop" : normaliseDeviceValue(deviceType);
  if (isIPadOS) type = "tablet";
  return {
    device: {
      type,
      brand: isIPadOS ? "Apple" : normaliseDeviceValue(mobileVendor),
      model_name: isIPadOS ? "iPad" : normaliseDeviceValue(mobileModel),
    },
  };
};

const getScreenResolution = (): string =>
  `${window.screen.width}x${window.screen.height}@${
    window.devicePixelRatio || 1
  }`;

/**
 * Shared Faro options for every app (AX-8506):
 * - trackGeolocation: country/city resolved by the Faro receiver from the client IP
 * - device meta + screen resolution (page attribute) for the device breakdowns
 * Page views come from `setView` (view_changed) in each app's routes. The SDK's
 * experimental.trackNavigation is left off on purpose: it keeps a document-wide
 * MutationObserver and patches XHR/fetch for the whole session (costly in the worklist editor).
 */
export const getFaroBaseConfig = (): Pick<
  BrowserConfig,
  "trackGeolocation" | "metas" | "pageTracking"
> => ({
  trackGeolocation: true,
  metas: [faroDeviceMeta],
  pageTracking: {
    page: { attributes: { screen_resolution: getScreenResolution() } },
  },
});
